<script lang="ts">
  import type { PlayerProfile } from '@2k48/shared';
  import { createMotionScope, staggerIn } from '../../game/motion';
  import TopBar from '../components/TopBar.svelte';
  import PixelPanel from '../components/PixelPanel.svelte';
  import { api } from '../../net/client';
  import { i18n } from '../i18n/index.svelte';
  import { session } from '../stores/session.svelte';
  import { router } from '../stores/router.svelte';
  import { groupNumber, ordinalRank, shortDate } from '../format';

  /**
   * Personal bests, not a leaderboard. Everything is server-computed: the client
   * asks for its own runs and renders them, and never ranks anything itself.
   */

  interface BestRun {
    matchId: string;
    modeId: string;
    endedAt: number;
    score: number;
    maxTile: number;
    maxCombo: number;
  }

  interface HighScoresResponse {
    modeId: string;
    runs: BestRun[];
    totals: {
      matchesPlayed: number;
      bestScore: number;
      maxTile: number;
      maxCombo: number;
    };
  }

  const t = $derived(i18n.t);

  let data = $state<HighScoresResponse | null>(null);
  let owner = $state<PlayerProfile | null>(null);
  let loading = $state(true);
  const playerId = $derived(router.current.params.player || session.profile?.id);
  const isOwnProfile = $derived(playerId === session.profile?.id);

  $effect(() => {
    const player = playerId;
    data = null;
    owner = null;
    if (!player) {
      loading = false;
      return;
    }

    let cancelled = false;
    loading = true;

    void Promise.all([
      api<HighScoresResponse>(`/highscores/${player}`),
      api<{ profile: PlayerProfile }>(`/profile/${player}`),
    ])
      .then(([scores, profile]) => {
        if (cancelled) return;
        data = scores;
        owner = profile.profile;
      })
      .catch(() => {
        if (cancelled) return;
        data = null;
        owner = null;
      })
      .finally(() => {
        if (!cancelled) loading = false;
      });

    return () => {
      cancelled = true;
    };
  });

  /**
   * The rows arrive together from one response, so they stagger in as a group
   * once — not per-row as data trickles, which is what makes a list look like
   * it is still loading after it has finished.
   */
  let listRoot: HTMLElement | undefined = $state();
  const scope = createMotionScope();

  $effect(() => {
    if (!data || data.runs.length === 0 || !listRoot) return;
    scope.add(() => staggerIn([...listRoot!.children] as HTMLElement[], 'right'));
  });

  $effect(() => () => scope.destroy());

  const best = $derived(data?.runs[0] ?? null);
  const name = $derived((owner?.displayName ?? session.profile?.displayName ?? 'YOU').slice(0, 3));
</script>

<div class="view highscores">
  <TopBar title={t.highScores.title} />

  <div class="view__body">
    {#if loading}
      <p class="view__placeholder">{t.common.loading}</p>
    {:else if !data}
      <p class="view__placeholder">
        {isOwnProfile ? t.highScores.offlineMessage : t.highScores.offlineMessagePublic}
      </p>
    {:else if data.runs.length === 0}
      <!-- An empty table with a zeroed hero above it would read as "your record
           is nothing"; this says which mode is being counted, which is the part
           the player cannot otherwise see. -->
      <p class="view__placeholder">
        {isOwnProfile ? t.highScores.emptyMessage : t.highScores.emptyMessagePublic}
      </p>
    {:else}
      <PixelPanel variant="accent" class="highscores__hero">
        <span class="lbl">
          {isOwnProfile ? t.highScores.bestScore : t.highScores.bestScorePublic}
        </span>
        <span class="highscores__hero-value">{groupNumber(data.totals.bestScore)}</span>
        <span class="highscores__hero-tags">
          <span class="badge badge--cyan">{best?.maxTile || '-'}</span>
          <span class="badge">{t.common.combo(best?.maxCombo ?? 0)}</span>
        </span>
      </PixelPanel>

      <div class="highscores__head">
        <span>{t.common.rank}</span>
        <span>{t.common.name}</span>
        <span>{t.common.score}</span>
        <span>{t.common.date}</span>
      </div>

      <ol class="highscores__list" bind:this={listRoot}>
        {#each data.runs as run, index (run.matchId)}
          <li class="highscores__row" data-rank={index + 1}>
            <span class="highscores__rank">{ordinalRank(index + 1)}</span>
            <span class="highscores__name">{name}</span>
            <span class="highscores__score">{groupNumber(run.score)}</span>
            <span class="highscores__date">{shortDate(run.endedAt)}</span>
          </li>
        {/each}
      </ol>

      <div class="highscores__totals">
        <span class="stat">
          <span class="stat__label">{t.common.matches}</span>
          <span class="stat__value">{groupNumber(data.totals.matchesPlayed)}</span>
        </span>
        <span class="stat">
          <span class="stat__label">{t.common.maxTile}</span>
          <span class="stat__value">{data.totals.maxTile || '-'}</span>
        </span>
        <span class="stat">
          <span class="stat__label">{t.common.maxCombo}</span>
          <span class="stat__value">x{data.totals.maxCombo}</span>
        </span>
      </div>
    {/if}
  </div>
</div>
